/**
 * Detects iOS-class browsers that frequently reject async clipboard writes but
 * still allow `execCommand('copy')` while the original tap gesture is active.
 */
const isIOSLikeDevice = (): boolean => {
    const navigatorRef = globalThis.navigator;
    if (!navigatorRef) {
        return false;
    }

    return /ipad|iphone|ipod/i.test(navigatorRef.userAgent)
        || (
            navigatorRef.platform === 'MacIntel'
            && navigatorRef.maxTouchPoints > 1
        );
};

/**
 * Attempts to copy text through the legacy `execCommand('copy')` pathway.
 * It first tries an in-gesture `copy` event interception, which avoids
 * moving focus away from the live editor on iOS. If the browser refuses to
 * dispatch that event without an active selection, it falls back to the
 * traditional hidden textarea workaround while preserving the current
 * selection and active element.
 */
const tryLegacyClipboardCopy = ({
    text,
    html,
}: {
    text: string,
    html?: string,
}): boolean => {
    if (
        typeof document === 'undefined'
        || !document.body
        || typeof document.execCommand !== 'function'
    ) {
        return false;
    }

    const activeElement = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const selection = typeof document.getSelection === 'function'
        ? document.getSelection()
        : null;
    const isIOSDevice = isIOSLikeDevice();
    const preservedRange = selection && selection.rangeCount > 0
        ? selection.getRangeAt(0).cloneRange()
        : null;
    let interceptedClipboardWrite = false;
    const handleCopy = (event: ClipboardEvent): void => {
        const { clipboardData } = event;
        if (!clipboardData) return;

        clipboardData.setData('text/plain', text);
        if (html) {
            clipboardData.setData('text/html', html);
        }
        event.preventDefault();
        interceptedClipboardWrite = true;
    };

    /**
     * iOS Safari can be particular about which DOM target is allowed to own
     * the active selection during `execCommand('copy')`. Try a focused
     * transient contenteditable first, then fall back to the older
     * textarea-selection trick before giving up.
     */
    const attemptSelectionTargetCopy = (useContentEditableTarget: boolean): boolean => {
        const copyTarget = useContentEditableTarget
            ? document.createElement('div')
            : document.createElement('textarea');
        copyTarget.setAttribute('aria-hidden', 'true');
        copyTarget.setAttribute('data-legacy-clipboard-target', 'true');
        copyTarget.style.position = 'fixed';
        copyTarget.style.top = '0';
        copyTarget.style.left = '0';
        copyTarget.style.width = '1px';
        copyTarget.style.height = '1px';
        copyTarget.style.padding = '0';
        copyTarget.style.border = '0';
        copyTarget.style.outline = '0';
        copyTarget.style.boxShadow = 'none';
        copyTarget.style.background = 'transparent';
        copyTarget.style.color = 'transparent';
        copyTarget.style.fontSize = '16px';
        copyTarget.style.overflow = 'hidden';
        copyTarget.style.pointerEvents = 'none';
        copyTarget.style.userSelect = 'text';
        copyTarget.style.webkitUserSelect = 'text';
        copyTarget.style.clipPath = 'inset(50%)';

        document.body.appendChild(copyTarget);
        try {
            if (useContentEditableTarget) {
                copyTarget.textContent = text;
                copyTarget.contentEditable = 'true';
                copyTarget.tabIndex = -1;
                try {
                    copyTarget.focus({ preventScroll: true });
                } catch (_error) {
                    copyTarget.focus();
                }

                const range = document.createRange();
                range.selectNodeContents(copyTarget);
                selection?.removeAllRanges();
                selection?.addRange(range);
            } else {
                (copyTarget as HTMLTextAreaElement).value = text;
                copyTarget.setAttribute('readonly', 'true');
                try {
                    (copyTarget as HTMLTextAreaElement).focus({ preventScroll: true });
                } catch (_error) {
                    (copyTarget as HTMLTextAreaElement).focus();
                }
                (copyTarget as HTMLTextAreaElement).select();
                (copyTarget as HTMLTextAreaElement).setSelectionRange(0, text.length);
            }

            const didCopy = document.execCommand('copy');
            return interceptedClipboardWrite || didCopy;
        } finally {
            document.body.removeChild(copyTarget);
        }
    };

    try {
        if (isIOSDevice) {
            /**
             * WebKit can ignore `clipboardData.setData()` on temporary
             * selection workarounds in iOS Safari. Let the browser perform its
             * default copy for the selected fallback target instead of
             * intercepting the `copy` event ourselves.
             */
            if (attemptSelectionTargetCopy(true)) {
                return true;
            }

            if (attemptSelectionTargetCopy(false)) {
                return true;
            }

            return false;
        }

        document.addEventListener('copy', handleCopy);
        /**
         * Desktop browsers can still honor a trusted `copy` command with
         * clipboardData interception, which lets us preserve richer payloads
         * before falling back to a selected textarea.
         */
        const didInterceptCopy = document.execCommand('copy');
        if (interceptedClipboardWrite || didInterceptCopy) {
            return true;
        }

        return attemptSelectionTargetCopy(false);
    } finally {
        if (selection) {
            selection.removeAllRanges();
            if (preservedRange) {
                selection.addRange(preservedRange);
            }
        }

        try {
            activeElement?.focus({ preventScroll: true });
        } catch (_error) {
            activeElement?.focus();
        }

        document.removeEventListener('copy', handleCopy);
    }
};

/**
 * Copies text through the legacy browser clipboard API and reports a
 * structured failure when the browser refuses every supported fallback.
 */
const copyTextWithLegacyClipboard = ({
    text,
    html,
}: {
    text: string,
    html?: string,
}): Promise<void> => (
    tryLegacyClipboardCopy({ text, html })
        ? Promise.resolve()
        : Promise.reject(new Error('Legacy clipboard copy failed'))
);

/**
 * Writes text or html to the clipboard, falling back to the legacy selection
 * path when the async Clipboard API is unavailable or rejects.
 */
const writeClipboardValue = ({
    text,
    html,
}: {
    text: string,
    html?: string,
}): Promise<void> => {
    const clipboard = globalThis.navigator?.clipboard;

    if (isIOSLikeDevice()) {
        /**
         * On iOS, try the synchronous legacy path first so the copy attempt
         * definitely stays inside the original press gesture. If that fails,
         * fall back to async clipboard writes when available.
         */
        if (tryLegacyClipboardCopy({ text, html })) {
            return Promise.resolve();
        }

        if (typeof clipboard?.writeText === 'function') {
            return clipboard.writeText(text);
        }

        return Promise.reject(new Error('Clipboard API unavailable'));
    }

    if (
        html
        && typeof clipboard?.write === 'function'
        && typeof ClipboardItem !== 'undefined'
    ) {
        return clipboard.write([
            new ClipboardItem({
                'text/plain': new Blob([text], { type: 'text/plain' }),
                'text/html': new Blob([html], { type: 'text/html' }),
            }),
        ]).catch(() => (
            typeof clipboard.writeText === 'function'
                ? clipboard.writeText(text).catch(() => copyTextWithLegacyClipboard({ text, html }))
                : copyTextWithLegacyClipboard({ text, html })
        ));
    }

    if (typeof clipboard?.writeText === 'function') {
        return clipboard.writeText(text).catch(() => copyTextWithLegacyClipboard({ text, html }));
    }

    return copyTextWithLegacyClipboard({ text, html });
};

// Reference: https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
const copyToClipboard = (
    text: string,
    onComplete?: () => void,
    onError?: (error: unknown) => void,
    html?: string,
): void => {
    let writePromise: Promise<void>;

    try {
        writePromise = writeClipboardValue({
            text,
            html,
        });
    } catch (error) {
        if (onError) onError(error);
        return;
    }

    writePromise.then(() => {
        if (onComplete) onComplete();
    }, (err) => {
        if (onError) onError(err);
    });
};

export default copyToClipboard;
